// src/lib/authFetch.js
// Drop-in fetch() replacement that auto-attaches the Supabase JWT.
// Use this for all /api/* calls so server-side auth guards pass.
//
// Usage:
//   import { authFetch } from "../lib/authFetch";
//   const res = await authFetch(`/api/proxy?url=${encodeURIComponent(url)}`);

import { supabase } from "./supabaseClient";

let _cachedToken  = null;
let _tokenExpiry  = 0;    // epoch ms

async function getToken() {
  const now = Date.now();
  // Reuse cached token if still valid (with 60s buffer)
  if (_cachedToken && now < _tokenExpiry - 60_000) return _cachedToken;

  if (!supabase) return null;
  try {
    const { data } = await supabase.auth.getSession();
    const session  = data?.session;
    if (!session) { _cachedToken = null; _tokenExpiry = 0; return null; }
    _cachedToken = session.access_token;
    // Supabase tokens expire in 3600s; store the exact expiry
    _tokenExpiry = (session.expires_at ?? 0) * 1000 || now + 3_500_000;
    return _cachedToken;
  } catch {
    return null;
  }
}

export async function authFetch(input, init = {}) {
  const token = await getToken();
  const headers = new Headers(init.headers ?? {});
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetch(input, { ...init, headers });
}

// Clears the cache (call on sign-out)
export function clearAuthFetchCache() {
  _cachedToken = null;
  _tokenExpiry = 0;
}
